/**
 * Side-by-side: how a human sees a page vs. how a crawl bot reads it.
 *
 * Left = a faux rendered page (hero, image block, button). Right = the same
 * page reduced to the machine-readable structure a bot actually consumes
 * (headings, links, structured data, llms.txt). Pure CSS/HTML — no images.
 */
export function HumanVsBotDiagram() {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {/* Human view — a pretty rendered page */}
      <figure className="overflow-hidden rounded-xl border border-[var(--hub-color-line)] bg-[var(--hub-color-surface)]">
        <figcaption className="border-b border-[var(--hub-color-line)] bg-[var(--hub-color-surface-sunken)] px-4 py-2 text-xs font-semibold text-[var(--hub-color-ink-muted)]">
          What a person sees
        </figcaption>
        <div className="space-y-3 p-4" aria-hidden="true">
          {/* Spec 250 (review P1-15): a faux page in the paper palette — the
              Skagen blocks were colour as decoration. */}
          <div className="h-3 w-2/3 rounded-[var(--hub-radius-control)] bg-[var(--hub-color-ink-subtle)]" />
          <div className="h-2 w-full rounded-[var(--hub-radius-control)] bg-[var(--hub-color-line)]" />
          <div className="h-2 w-5/6 rounded-[var(--hub-radius-control)] bg-[var(--hub-color-line)]" />
          <div className="flex h-20 items-center justify-center rounded-lg bg-[var(--hub-color-surface-sunken)] text-xs font-medium text-[var(--hub-color-ink-muted)]">
            hero image
          </div>
          <div className="flex gap-2">
            <div className="h-7 w-24 rounded-full bg-[var(--hub-color-ink)]" />
            <div className="h-7 w-20 rounded-full border border-[var(--hub-color-line-strong)]" />
          </div>
        </div>
      </figure>

      {/* Bot view — stripped, machine-readable text. Kridt has no
          always-dark decorative surface (the system doesn't use dark
          decorative panels), so this reads as a sunken code/terminal
          region instead — the idiom the system does have for this content:
          a sunken surface, a hairline, ink text, and JetBrains Mono where
          characters must align. It now flips correctly with the theme
          instead of staying dark while its own text stays tuned for dark. */}
      <figure className="overflow-hidden rounded-xl border border-[var(--hub-color-line)] bg-[var(--hub-color-surface-sunken)]">
        <figcaption className="border-b border-[var(--hub-color-line)] px-4 py-2 text-xs font-semibold text-[var(--hub-color-ink-muted)]">
          What a bot reads
        </figcaption>
        {/* Spec 250 (review P1-9): mono only for literal code — the tags and
            file names — at the code size. "link" and "structured data" are
            words, so they are prose; the tone colours on them were decoration. */}
        <div className="space-y-2 p-4 text-sm leading-relaxed text-[var(--hub-color-ink)]">
          <p><code className="hub-code">&lt;h1&gt;</code> Best running shoes for flat feet</p>
          <p><code className="hub-code">&lt;h2&gt;</code> Top picks, how we tested</p>
          <p><span className="font-semibold">Link</span> to /reviews/stability-shoes</p>
          <p><span className="font-semibold">Structured data</span> Product, Review, FAQ</p>
          <p><code className="hub-code">robots.txt</code> and <code className="hub-code">llms.txt</code> guide crawlers</p>
        </div>
      </figure>
    </div>
  );
}
